import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useHttpClient } from '@volverjs/data/vue'
import { BlockBlobClient } from '@azure/storage-blob'
import {
    MediaType,
    type Media,
    type MediaDownloadHandle,
    type MediaSource,
    type MediaUploadHandle,
} from 'models'
import { useMediaRawOptions } from './useMediaRawOptions'
import { Audience } from 'ability'
const { getRawOptions } = useMediaRawOptions()

export const callPrepareUpload = async (
    file: File,
    storedFileName?: string,
    audience: Audience = Audience.Backoffice,
) => {
    const { client: httpClient } = useHttpClient()
    const rawOptions = await getRawOptions(file)
    const extension = file.name.split('.').pop()
    const response = await httpClient.post(
        {
            template: `api/v${import.meta.env.VITE_API_VERSION}/:audience/medias/prepare-upload`,
            params: {
                audience,
            },
        },
        {
            json: {
                type: rawOptions?.type || MediaType.binary,
                name: file.name,
                mimeType: file.type,
                bytesCount: file.size,
                extension: extension ? `.${extension}` : '.bin',
                storedFileName,
            },
        },
    )
    return await response.json<MediaUploadHandle>()
}

export const callPrepareDownload = async (
    mediaSource: MediaSource,
    audience: Audience = Audience.Public,
) => {
    const { client: httpClient } = useHttpClient()
    const response = await httpClient.post(
        {
            template: 'api/v1/:audience/medias/prepare-download',
            params: {
                audience,
            },
        },
        {
            timeout: false,
            json: mediaSource,
        },
    )
    return await response.json<MediaDownloadHandle>()
}

export const uploadFileToBlob = async (
    file: File,
    handle: MediaUploadHandle,
): Promise<boolean> => {
    try {
        await new BlockBlobClient(handle.uploadHandle.uploadSasuri).uploadData(
            file,
            {
                metadata: handle.uploadHandle.metadata,
                tags: handle.uploadHandle.tags,
                maxSingleShotSize: 5 * 1024 * 1024,
                concurrency: 5,
                blobHTTPHeaders: {
                    blobContentType: file.type,
                },
            },
        )
        return true
    } catch (_error) {
        return false
    }
}

export const createMediaFromFile = async (
    file: File,
): Promise<Media | null> => {
    try {
        const uploadHandle = await callPrepareUpload(file)
        const uploaded = await uploadFileToBlob(file, uploadHandle)
        if (!uploaded) {
            return null
        }
        const rawOptions = await getRawOptions(file)
        return {
            name: file.name,
            src: uploadHandle.src,
            rawOptions,
            srcSet: [],
        }
    } catch (_error) {
        return null
    }
}

export const useMediaStore = defineStore('media', () => {
    const downloads = ref<Map<string, MediaDownloadHandle>>(new Map())

    const addDownloadHandle = (
        mediaSource: MediaSource,
        mediaDownloadHandle: MediaDownloadHandle,
    ) => {
        downloads.value.set(mediaSource.storedFileName, mediaDownloadHandle)
    }

    const getDownloadHandle = async (
        mediaSource: MediaSource,
        audience: Audience = Audience.Public,
    ) => {
        let toReturn = downloads.value.get(mediaSource.storedFileName)
        if (toReturn) {
            if (new Date(toReturn.downloadHandle.expiresOn) > new Date()) {
                return toReturn
            }
            downloads.value.delete(mediaSource.storedFileName)
        }
        toReturn = await callPrepareDownload(mediaSource, audience)
        if (toReturn) {
            downloads.value.set(mediaSource.storedFileName, toReturn)
            return toReturn
        }
        return undefined
    }

    const hasDownloadHandle = (mediaSource: MediaSource) => {
        return getDownloadHandle(mediaSource) !== undefined
    }

    return {
        addDownloadHandle,
        getDownloadHandle,
        getUploadHandle: callPrepareUpload,
        hasDownloadHandle,
        uploadFileToBlob,
        createMediaFromFile,
    }
})
